import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X } from "lucide-react";
import Loader from "../components/Loader";
import TemplateCard from "../components/VacancyStatuses/TemplateCard";
import {
  useVacancyStatusTemplates,
  useVacancyStatusTemplateById,
} from "@/hooks/queries/useVacancyStatusTemplates";
import { useDeleteVacancyStatusTemplate } from "@/hooks/mutations/vacancyStatusTemplates";
import useToastStore from "@/components/Common/Toast/toastStore";

const VacancyStatuses = () => {
  const navigate = useNavigate();
  const { showToast } = useToastStore;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Fetch all templates
  const {
    data: templatesResponse,
    isLoading,
    isError,
  } = useVacancyStatusTemplates();
  const deleteTemplateMutation = useDeleteVacancyStatusTemplate();
  const templates = templatesResponse?.data || [];

  // Fetch selected template details for modal
  const { data: selectedTemplateResponse } = useVacancyStatusTemplateById(
    selectedTemplateId,
    {
      enabled: !!selectedTemplateId,
    },
  );
  const selectedTemplate =
    selectedTemplateResponse?.data || selectedTemplateResponse;

  const handleEditTemplate = (template) => {
    navigate(`/vacancy/Statuses/builder/${template.id}`);
  };

  const handleDeleteTemplate = (templateId) => {
    setTemplateToDelete(templateId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTemplateMutation.mutateAsync(templateToDelete);
      showToast({
        message: "Template deleted successfully!",
        type: "success",
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error deleting template:", error);
      showToast({
        message: error.message || "Failed to delete template",
        type: "error",
      });
      setIsDeleting(false);
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    }
  };

  const handleViewTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    setShowTemplateModal(true);
  };

  const closeModal = () => {
    setShowTemplateModal(false);
    setSelectedTemplateId(null);
  };

  if (isLoading || isDeleting) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading templates..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-muted p-4">
        Error loading templates. Please try again.
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12  px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Vacancy Statuses
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Manage vacancy status templates, defining recruitment stages and
                visual identifiers for each phase.
              </p>
            </div>
            <button
              onClick={() => {
                navigate("/vacancy/Statuses/builder");
              }}
              className="bg-primary-dark text-white px-4 h-[36px] rounded-lg shadow-sm font-semibold transition-all hover:bg-primary-dark flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Status Template
            </button>
          </div>
        </div>

        {/* Templates List */}
        {templates.length === 0 ? (
          <div className="flex justify-center items-center h-[calc(100vh-200px)]">
            <h3 className="text-lg font-medium text-text-foreground">
              No Templates Found
            </h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onView={() => handleViewTemplate(template.id)}
                onEdit={() => handleEditTemplate(template)}
                onDelete={() => handleDeleteTemplate(template.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Template View Modal */}
      {showTemplateModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center overflow-y-auto">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl p-8 my-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="flex justify-between items-center border-b pb-4">
              <h2 className="text-2xl font-bold text-foreground">
                {selectedTemplate.title}
              </h2>
              <button
                onClick={closeModal}
                className="text-muted-foreground hover:text-foreground focus:outline-none"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                Stages:
              </h3>
              {selectedTemplate.status &&
              Array.isArray(selectedTemplate.status) &&
              selectedTemplate.status.length > 0 ? (
                <div className="space-y-3">
                  {selectedTemplate.status.map((stage, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 border border-border rounded-lg"
                    >
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: stage.bg_color || "#9ca3af" }}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-foreground">
                          {stage.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Order: {stage.p_order}
                        </p>
                      </div>
                      <div
                        className="px-3 py-1 rounded text-sm font-medium"
                        style={{
                          backgroundColor: stage.bg_color
                            ? `${stage.bg_color}20`
                            : "#f3f4f6",
                          color: stage.bg_color || "#374151",
                        }}
                      >
                        {stage.bg_color || "No color"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  No stages defined for this template.
                </p>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={closeModal}
                className="bg-primary-dark text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="relative z-10"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="fixed inset-0 bg-foreground/40 transition-opacity"
            aria-hidden="true"
          ></div>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-card text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg
                        className="h-6 w-6 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                        />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3
                        className="text-base font-semibold text-foreground"
                        id="modal-title"
                      >
                        Delete Template
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-muted-foreground">
                          Are you sure you want to delete this template? This
                          action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-muted px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setTemplateToDelete(null);
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-card px-3 py-2 text-sm font-semibold text-foreground ring-1 shadow-xs ring-input ring-inset hover:bg-muted sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VacancyStatuses;
